// Per spec/design-system.md §Primitive 4.
//
// **P-037.18 D-118 — default color changed.** The system-wide "≥18 px
// headlines render in #041e42" convention applies to every heading
// surface (SectionHeader 21 px, commentary headline 21 px, event-card /
// feature-card / sponsor-card h3 18 px, footer chapter-name h3 18 px,
// etc.). All current heading() consumers render at ≥18 px, so the
// primitive default flips from `tokens.color['ypo-navy']` (#0d2b53) to
// `tokens.color['accent-navy-deep']` (#041e42). Explicit `color`
// overrides at the call site still take precedence (commentary +
// SectionHeader pass the color explicitly for documentation clarity).
//
// **P-037.23 D-131 — `text` accepts `string | HtmlString`.** Matches
// the `text()` primitive (which has always accepted both) and the
// `link()` primitive's `text` slot. Plain strings are escaped on
// interpolation by the html kernel; HtmlString passes through trusted
// (D-021 + D-022). This lets consumers compose anchors inside headings
// (e.g., `sponsor-card`'s sponsor-name-as-link arm) without hand-rolling
// the surrounding `<h3>` byte-shape.

import { html } from '../render'
import { tokens } from '../tokens'
import { HtmlString } from '../types'

export type HeadingLevel = 1 | 2 | 3
export type HeadingAlign = 'left' | 'center' | 'right'

export interface HeadingProps {
  text: string | HtmlString
  level?: HeadingLevel
  color?: string
  align?: HeadingAlign
  size?: number
  weight?: number
}

export function heading({
  text,
  level = 2,
  color = tokens.color['accent-navy-deep'],
  align = 'left',
  size,
  weight = tokens.type.weight.bold,
}: HeadingProps): HtmlString {
  const finalSize =
    size ??
    (level === 1 ? tokens.type.size.xl : level === 2 ? tokens.type.size.lg : tokens.type.size.md)
  const style = `margin: 0; font-family: ${tokens.type.fontFamily}; font-size: ${finalSize}px; font-weight: ${weight}; line-height: ${tokens.type.leading.tight}; color: ${color}; text-align: ${align};`
  // prettier-ignore
  return level === 1
    ? html`<h1 style="${style}">${text}</h1>`
    : level === 2
      ? html`<h2 style="${style}">${text}</h2>`
      : html`<h3 style="${style}">${text}</h3>`
}
